import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@zond/ui';
import { cn } from '@zond/utils';
import { Check, Languages } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from './language';
import { useLanguage } from './language-provider';

interface LanguageSwitcherProps {
  /** Extra classes for the trigger button (e.g. positioning). */
  className?: string;
  /** Visual style of the trigger button. */
  variant?: 'ghost' | 'outline';
  /** Show the current language label next to the globe icon. Icon-only by default. */
  showLabel?: boolean;
}

/**
 * Standalone language switcher usable anywhere — including pre-auth pages
 * (login, sign-up, password flows) that live outside the dashboard layout.
 */
export const LanguageSwitcher = ({
  className,
  variant = 'outline',
  showLabel = false,
}: LanguageSwitcherProps) => {
  const { language, setLanguage } = useLanguage();
  const current = SUPPORTED_LANGUAGES.find((l) => l.value === language) ?? SUPPORTED_LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={showLabel ? 'sm' : 'icon'}
          className={cn(showLabel && 'gap-2', className)}
          aria-label={current.label}
        >
          <Languages className="size-4" />
          {showLabel && <span className="text-sm">{current.label}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40">
        {SUPPORTED_LANGUAGES.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => setLanguage(option.value)}
            className="cursor-pointer"
          >
            <span className="flex-1">{option.label}</span>
            {language === option.value && <Check className="size-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
